import React from 'react';
import { theme } from '../theme';

// <purpose>THE split pill (small facts on a row, 2026-10-19): one rounded pill with a KEY segment on
// the tint ground and a VALUE segment on white, divided by a hairline. One grammar for two families:
//   - the clerk traces (ClerkTrace) — the key is the act's ICON: the retry ring and „1×", a copy
//     glyph and „Kopiert", a pencil and „Bearbeitet", the envelope and „Eingang bestätigt";
//   - the AI descriptors — the key is a served NOUN in sentence case: „Vorschlagskonfidenz | 86 %";
//   - the footer letter check (variant `check`, 2026-10-30) — „Frist | nicht im Entwurf", pressable.
// Registry: design/design-languages.html, entries `bearbeitung_traces` and `ai_descriptors`.</purpose>
// <dependencies>theme tokens only — the pill's ink is navyMid, the key ground is navyTint, the
// border and the divider are borderLight.</dependencies>
// <constraints>Fixed height per variant (row 18 px, detail 20 px) so a pill landing late never grows
// its line; `flexShrink: 0` so a row's right-hand group stays whole while the left one gives way.
// The key never wraps and never recases a word: every key is sentence case, exactly as given.</constraints>
// `check` is the footer letter check's face (2026-10-30): the same split grammar at reading size, pressable.
export type SplitPillVariant = 'row' | 'detail' | 'check';

/** THE metrics every small-fact pill shares per variant — the split pill AND the decision badge
 *  (DecisionBadge imports these), so the two can never end up at two heights on one row. */
export const SMALL_FACT_METRICS: Record<SplitPillVariant, { fontSize: number; iconSize: number; height: number }> = {
  row:    { fontSize: 10, iconSize: 11, height: 18 },
  detail: { fontSize: 11, iconSize: 12, height: 20 },
  check:  { fontSize: 11, iconSize: 12, height: 22 },
};

interface SplitPillMetrics {
  fontSize: number;
  keyFontSize: number;
  keyPadding: string;
  valuePadding: string;
  iconSize: number;
}

const METRICS: Record<SplitPillVariant, SplitPillMetrics> = {
  row:    { ...SMALL_FACT_METRICS.row, keyFontSize: 9.5, keyPadding: '0 5px', valuePadding: '0 8px' },
  detail: { ...SMALL_FACT_METRICS.detail, keyFontSize: 10.5, keyPadding: '0 7px', valuePadding: '0 10px' },
  check:  { ...SMALL_FACT_METRICS.check, keyFontSize: 11, keyPadding: '0 8px', valuePadding: '0 8px' },
};

// The painted height per variant — the line box plus the 1 px border on each side. Matches the
// list row's 18 px line reservation (TicketRowMeta) so a trace landing changes no row height.
export const SPLIT_PILL_HEIGHT: Record<SplitPillVariant, number> = {
  row: SMALL_FACT_METRICS.row.height, detail: SMALL_FACT_METRICS.detail.height, check: SMALL_FACT_METRICS.check.height,
};

export const SplitPill: React.FC<{
  /** The key segment: an icon element, or a word (sentence case, as given). */
  icon?: React.ReactNode;
  label?: string;
  /** The value segment. */
  children: React.ReactNode;
  variant?: SplitPillVariant;
  testId?: string;
  style?: React.CSSProperties;
  className?: string;
  /** Present ⇒ the pill is a BUTTON (the footer check opens its card from it). */
  onClick?: () => void;
  /** The pill's card is open: the pill stays filled navy. */
  pressed?: boolean;
  /** A fact, not a finding: softer value ink, and no pointer even when pressable. */
  quiet?: boolean;
  ariaLabel?: string;
  title?: string;
}> = ({ icon, label, children, variant = 'row', testId, style, className, onClick, pressed, quiet, ariaLabel, title }) => {
  const m = METRICS[variant];
  const ink = pressed ? theme.colors.white : theme.colors.navyMid;
  const outer: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'stretch', boxSizing: 'border-box',
    height: SPLIT_PILL_HEIGHT[variant], padding: 0, margin: 0,
    border: `1px solid ${pressed ? theme.colors.navy : theme.colors.borderLight}`, borderRadius: variant === 'check' ? 999 : 10, overflow: 'hidden',
    backgroundColor: pressed ? theme.colors.navy : theme.colors.white, color: ink,
    ...(onClick ? { fontFamily: theme.font.family } : null), fontSize: m.fontSize, fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0,
    transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    ...(onClick ? { cursor: quiet ? 'default' : 'pointer' } : null),
    ...style,
  };
  const key = (
    <span
      style={{
        display: 'flex', alignItems: 'center', gap: 3,
        padding: m.keyPadding, backgroundColor: pressed ? theme.colors.navy : theme.colors.navyTint,
        borderRight: `1px solid ${pressed ? 'rgba(255,255,255,0.35)' : theme.colors.borderLight}`,
        fontSize: m.keyFontSize, fontWeight: 500,
        color: ink,
      }}
    >
      {icon ? <span style={{ display: 'inline-flex', width: m.iconSize, height: m.iconSize }}>{icon}</span> : null}
      {label}
    </span>
  );
  const value = (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: m.valuePadding,
      ...(variant === 'check' ? { fontWeight: quiet ? 500 : 600, color: pressed ? theme.colors.white : quiet ? theme.colors.navyMid : theme.colors.navy } : null),
    }}>
      {children}
    </span>
  );
  if (onClick) {
    return (
      <button type="button" data-testid={testId} className={className} onClick={onClick}
        aria-label={ariaLabel} aria-expanded={quiet ? undefined : !!pressed} title={title} style={outer}>
        {key}{value}
      </button>
    );
  }
  return (
    <span data-testid={testId} className={className} aria-label={ariaLabel} title={title} style={outer}>
      {key}{value}
    </span>
  );
};

/** The icon size a split-pill key draws its glyph at, per variant — so a caller passes the pill's own
 *  size to the icon component instead of guessing one. */
export const splitPillIconSize = (variant: SplitPillVariant = 'row'): number => METRICS[variant].iconSize;
